import React, { useContext, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Center,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Select,
  Spinner,
  Stack
} from '@chakra-ui/react'
import moment from 'moment'
import { useMutation } from 'urql'

import {
  Upup_CreateRentalAddOnDocument as CreateRentalAddOn,
  Upup_LiabilityTypesQuery as LiabilityTypesQuery
} from 'graphql/generated'
import { payments } from '@homevest/utils'
import { RentalDetailsContext } from '../../RentalDetailsContext'
import RentalAddOnScheduleFields from '../RentalAddOnScheduleFields'
import { getRentalAddOnFormErrors, hasRentalAddOnFormErrors } from '../validation'

type LiabilityType = LiabilityTypesQuery['liability_types'][number]

CreateRentalAddOnForm.propTypes = {
  onSuccess: PropTypes.func,
  onSubmit: PropTypes.func,
  rentalId: PropTypes.string.isRequired
}

export default function CreateRentalAddOnForm({
  onSuccess,
  onSubmit = () => {},
  rentalId,
  occupancyDate
}: {
  onSuccess?: () => void
  onSubmit: () => void
  rentalId: string
  occupancyDate: string
}) {
  const [liabilityType, setLiabilityType] = useState<LiabilityType | null>(null)
  const [price, setPrice] = useState('')
  const [startDate, setStartDate] = useState(moment().format('YYYY-MM-DD'))
  const [endDate, setEndDate] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false)

  const [{ fetching, error }, createRentalAddOn] = useMutation(CreateRentalAddOn)
  const { liabilityTypes } = useContext(RentalDetailsContext)

  const addOnLiabilityTypes = useMemo(
    () =>
      liabilityTypes
        .filter((lt) => lt.payment_category === payments.PAYMENT_CATEGORIES.ADD_ON)
        .sort((left, right) => left.name.localeCompare(right.name)),
    [liabilityTypes]
  )
  const errors = getRentalAddOnFormErrors({
    amount: price,
    endDate,
    hasLiabilityType: Boolean(liabilityType),
    occupancyDate,
    startDate
  })
  const isDirty = Boolean(liabilityType) || price !== '' || endDate !== ''
  const showErrors = hasAttemptedSubmit || isDirty
  const isValid = !hasRentalAddOnFormErrors(errors)

  const submitRentalAddOn = async () => {
    setHasAttemptedSubmit(true)

    if (!isValid) {
      return
    }

    const confirmed = window.confirm(`Are you sure you want to create the rental add on?`)

    if (!confirmed) {
      return
    }

    setIsSaving(true)
    const result = await createRentalAddOn({
      liability_type_id: liabilityType!.id,
      rental_id: rentalId,
      start_date: startDate,
      end_date: endDate || null,
      price: Number(price)
    })

    setIsSaving(false)
    if (result.error === undefined) {
      setLiabilityType(null)
      setPrice('')
      setStartDate(moment().format('YYYY-MM-DD'))
      setEndDate('')
      setHasAttemptedSubmit(false)
      onSubmit()
      onSuccess?.()
    }
  }

  return (
    <React.Suspense
      fallback={
        <Center py={12}>
          <Spinner />
        </Center>
      }
    >
      <Stack spacing={5}>
        {error && (
          <Alert status='error'>
            <AlertIcon />
            {error.message}
          </Alert>
        )}

        <FormControl
          isRequired
          isInvalid={showErrors && Boolean(errors.liabilityType)}
          isDisabled={fetching || isSaving}
        >
          <FormLabel>Add-on type</FormLabel>
          <Select
            placeholder='Select an add-on type'
            value={liabilityType?.id || ''}
            onChange={(event) => {
              const nextLiabilityType =
                addOnLiabilityTypes.find((lt) => lt.id === event.target.value) || null

              setLiabilityType(nextLiabilityType)
              if (nextLiabilityType?.price !== null && nextLiabilityType?.price !== undefined) {
                setPrice(`${nextLiabilityType.price}`)
              }
            }}
          >
            {addOnLiabilityTypes.map((lt) => (
              <option key={lt.id} value={lt.id}>
                {lt.name}
              </option>
            ))}
          </Select>
          <FormErrorMessage>{errors.liabilityType}</FormErrorMessage>
        </FormControl>

        <RentalAddOnScheduleFields
          amount={price}
          endDate={endDate}
          errors={errors}
          isAmountDisabled={!liabilityType?.is_variable}
          isDisabled={fetching || isSaving}
          onAmountChange={setPrice}
          onEndDateChange={setEndDate}
          onStartDateChange={setStartDate}
          showErrors={showErrors}
          startDate={startDate}
        />

        <Box display='flex' justifyContent='flex-end'>
          <Button
            colorScheme='teal'
            onClick={submitRentalAddOn}
            isDisabled={!isValid || fetching || isSaving}
            isLoading={fetching || isSaving}
          >
            Create Rental Add-On
          </Button>
        </Box>
      </Stack>
    </React.Suspense>
  )
}
